import moment from 'moment';
import 'moment/locale/ja';

export const getUTCStringNow = () => {
    return moment().toDate().toISOString();
};

export const getDayFromDate = (value: string) => {
    return moment(value).toDate().getDay();
};

export const getDate = () => {
    return moment().toDate();
};

export const getYear = () => {
    return moment().year();
};

export const getDateInMonth = (value: string, toFormat: string = 'YYYY/MM/DD') => {
    return moment(value, toFormat).daysInMonth();
};

export const getDateNow = (toFormat: string = 'YYYY/MM/DD') => {
    return moment().local().format(toFormat);
};

export const formatTimestamp = (timestamp: number, toFormat: string = 'YYYY/MM/DD') => {
    if (timestamp) {
        return moment(timestamp).format(toFormat);
    }

    return '';
};

export const formatDate = (date: string | Date, toFormat: string = 'YYYY/MM/DD') => {
    if (date) {
        return moment(date).format(toFormat);
    }

    return '';
};

export const formatTime = (time: string, toFormat: string = 'HH:mm') => {
    if (time) {
        return moment(time).format(toFormat);
    }
    return '';
};

export const formatFromTime = (
    time: string,
    fromFormat: string = 'HH:mm',
    toFormat: string = 'HH:mm',
) => {
    if (time) {
        return moment(time, fromFormat).format(toFormat);
    }

    return '';
};

export const formatToDate = (date: string, fromFormat: string = 'YYYY/MM/DD') => {
    if (date) {
        return moment(date, fromFormat).toDate();
    }

    return null;
};

export const formatToDateTime = (date: string, fromFormat: string = 'YYYY/MM/DD HH:mm') => {
    if (date) {
        return moment(date, fromFormat).toDate();
    }

    return null;
};

export const formatToDateOnly = (date: string, fromFormat: string = 'YYYY/MM/DD') => {
    if (date) {
        return moment(date, fromFormat).startOf('day').toDate();
    }

    return null;
};

export const diffYears = (
    fromDate: string,
    toDate: string = getUTCStringNow(),
    fromFormat: string = 'YYYY',
) => {
    if (fromDate && toDate) {
        const dateEnd = moment(toDate).format(fromFormat);
        const dateStart = moment(fromDate).format(fromFormat);
        const years = moment(dateEnd, fromFormat).diff(moment(dateStart, fromFormat), 'years');

        return years;
    }

    return null;
};

export const isValid = (date: string, fromFormat: string = 'YYYY/MM/DD') => {
    if (date) {
        return moment(date, fromFormat).isValid();
    }

    return false;
};

export const isValidDate = (date: Date) => {
    if (date) {
        return moment(date).isValid();
    }

    return false;
};

export const compareDateSameOrAfter = (
    from_date: string,
    to_date: string,
    fromFormat: string = 'YYYY-MM-DD HH:mm:ss',
) => {
    return moment(from_date, fromFormat).isSameOrAfter(moment(to_date, fromFormat));
};

export const compareDateSame = (
    from_date: string,
    to_date: string,
    fromFormat: string = 'YYYY-MM-DD HH:mm:ss',
) => {
    return moment(from_date, fromFormat).isSame(moment(to_date, fromFormat));
};

export const compareDateSameOrBefore = (
    from_date: string,
    to_date: string,
    fromFormat: string = 'YYYY-MM-DD HH:mm:ss',
) => {
    return moment(from_date, fromFormat).isSameOrBefore(moment(to_date, fromFormat));
};

export const compareDateBefore = (
    from_date: string,
    to_date: string,
    fromFormat: string = 'YYYY-MM-DD HH:mm:ss',
) => {
    return moment(from_date, fromFormat).isBefore(moment(to_date, fromFormat));
};

export const formatFromText = (date: string) => {
    return moment(
        `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}`,
    ).toDate();
};

export const calculateAge = (date: string) => {
    let age: number = 0;
    if (date) {
        const today = getDate();
        const birthDate = formatToDate(date, 'YYYY-MM-DD');

        if (birthDate) {
            age = today.getFullYear() - birthDate.getFullYear();
            const month = today.getMonth() - birthDate.getMonth();
            if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
        }
    }

    return age;
};

export const subtractDate = (
    from: string,
    subtract_days: number,
    toFormat: string = 'YYYY-MM-DD HH:mm:ss',
) => {
    return moment(from).subtract(subtract_days, 'days').format(toFormat);
};

export const addDate = (
    from: string,
    add_days: number,
    toFormat: string = 'YYYY-MM-DD HH:mm:ss',
) => {
    return moment(from).add(add_days, 'days').format(toFormat);
};

export const startOfMonth = (from: string, toFormat: string = 'YYYY-MM-DD HH:mm:ss') => {
    return moment(from).startOf('month').format(toFormat);
};

export const endOfMonth = (from: string, toFormat: string = 'YYYY-MM-DD HH:mm:ss') => {
    return moment(from).endOf('month').format(toFormat);
};

export const formatToUTC = (date: string, format = 'YYYY-MM-DD') => {
    return moment(date).utc().format(format);
};

export const formatArrivalAt = (value: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';

    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    const hh = String(date.getUTCHours()).padStart(2, '0');
    const mi = String(date.getUTCMinutes()).padStart(2, '0');
    const ss = String(date.getUTCSeconds()).padStart(2, '0');

    return `${yyyy}/${mm}/${dd} ${hh}:${mi}:${ss}`;
};
